import React, { useEffect } from 'react';
import { useSearchParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { getPurchaseDetails } from '../../api/endpoints/checkoutv2';
import { useAnalytics } from '../../hooks/useAnalytics';
import useCartStore from '../../store/cartStore';
import Loader from '../../components/Loader';

// Product-specific configuration
const getProductConfig = (sku: string) => {
  switch (sku) {
    case 'PDF2WORD':
      return {
        name: 'PDF2WORD Edition',
        downloadUrl: 'https://pdf2xl.com/download/PDF2Word.msi',
        helpLinks: [
          {
            title: 'Using PDF2WORD',
            url: 'https://help.pdf2xl.com/en/articles/5802845-converting-with-pdf2word-2-1',
            icon: '/checkout/assets/img/using_pdf2xl.png',
          },
          {
            title: 'User Dashboard',
            url: 'https://pdf2xl.com/app/#/auth/login',
            icon: '/checkout/assets/img/user_dashboard.png',
          },
          {
            title: 'Converting a Scanned PDF',
            url: 'https://help.pdf2xl.com/en/articles/5807391-converting-a-scanned-pdf-with-pdf2word-2-1',
            icon: '/checkout/assets/img/converting_101.png',
          },
          {
            title: 'Tech Support',
            url: 'https://pdf2xl.com/support/',
            icon: '/checkout/assets/img/tech_support.png',
          },
        ],
      };
    case 'SPLTMRGPRO':
      return {
        name: 'Split/Merge Edition',
        downloadUrl: 'https://pdf2xl.com/download-split-and-merge-pdf/',
        helpLinks: [
          {
            title: 'Using PDF2XL',
            url: 'https://help.pdf2xl.com/using-pdf2xl',
            icon: '/checkout/assets/img/using_pdf2xl.png',
          },
          {
            title: 'User Dashboard',
            url: 'https://pdf2xl.com/app/#/auth/login',
            icon: '/checkout/assets/img/user_dashboard.png',
          },
          {
            title: 'converting 101',
            url: 'https://help.pdf2xl.com/converting-101',
            icon: '/checkout/assets/img/converting_101.png',
          },
          {
            title: 'Tech Support',
            url: 'https://pdf2xl.com/support/',
            icon: '/checkout/assets/img/tech_support.png',
          },
        ],
      };
    default:
      return {
        name: 'PDF2XL Edition',
        downloadUrl: 'https://pdf2xl.com/download-pdf2xl/',
        helpLinks: [
          {
            title: 'Using PDF2XL',
            url: 'https://help.pdf2xl.com/using-pdf2xl',
            icon: '/checkout/assets/img/using_pdf2xl.png',
          },
          {
            title: 'User Dashboard',
            url: 'https://pdf2xl.com/app/#/auth/login',
            icon: '/checkout/assets/img/user_dashboard.png',
          },
          {
            title: 'converting 101',
            url: 'https://help.pdf2xl.com/converting-101',
            icon: '/checkout/assets/img/converting_101.png',
          },
          {
            title: 'Tech Support',
            url: 'https://pdf2xl.com/support/',
            icon: '/checkout/assets/img/tech_support.png',
          },
        ],
      };
  }
};

const ThanksPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const { trackPurchase, trackUetPurchase, trackPageView } = useAnalytics();
  const clearCart = useCartStore((state) => state.clearCart);

  // Track page view
  useEffect(() => {
    trackPageView('/purchase/thanks', 'Order Complete');
  }, [trackPageView]);

  // Fetch purchase details
  const {
    data: purchase,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ['purchase', sessionId],
    queryFn: () => {
      if (!sessionId) {
        throw new Error('Session ID is required');
      }
      return getPurchaseDetails({ session_id: sessionId });
    },
    enabled: !!sessionId,
    retry: 3,
    retryDelay: 1000,
  });

  // Track purchase analytics
  useEffect(() => {
    if (purchase) {
      console.log('[ThanksPage] Purchase details:', purchase);

      const quantity = purchase.quantity || 1;
      const totalValue = Number(purchase.amount) / 100;

      // Validate totalValue before tracking
      if (!isNaN(totalValue) && isFinite(totalValue) && totalValue > 0) {
        // Track GA4 purchase event with all required fields
        trackPurchase({
          transaction_id: purchase.sessionId,
          value: totalValue,
          currency: purchase.currency.toUpperCase(),
          coupon: purchase.coupon ? (purchase.coupon.parent || purchase.coupon.id) : undefined,
          items: [
            {
              item_id: purchase.productSku,
              item_name: purchase.productName,
              item_brand: 'PDF2XL',
              item_category: 'Plan',
              item_variant: purchase.interval || '',
              price: totalValue / quantity,
              quantity: quantity,
            },
          ],
        });

        // Track Microsoft UET purchase
        trackUetPurchase(totalValue, purchase.currency.toUpperCase());
      } else {
        console.error('[ThanksPage] Invalid totalValue:', {
          amount: purchase.amount,
          totalValue,
        });
      }

      // Clear cart after successful purchase
      clearCart();
    }
  }, [purchase, trackPurchase, trackUetPurchase, clearCart]);

  // Error state
  if (!sessionId) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="max-w-md w-full bg-white shadow-lg rounded-lg p-8 text-center">
          <div className="text-6xl mb-4">⚠️</div>
          <h1 className="text-2xl font-bold text-gray-900 mb-4">
            Missing Session ID
          </h1>
          <p className="text-gray-600 mb-6">
            We couldn't find your purchase session. Please check your email for
            confirmation.
          </p>
          <a
            href="/"
            className="inline-block px-6 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 transition-colors"
          >
            Go Home
          </a>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="max-w-md w-full bg-white shadow-lg rounded-lg p-8 text-center">
          <div className="text-6xl mb-4">⚠️</div>
          <h1 className="text-2xl font-bold text-gray-900 mb-4">
            Unable to Load Purchase
          </h1>
          <p className="text-gray-600 mb-6">
            {error instanceof Error ? error.message : 'An error occurred'}
          </p>
          <p className="text-sm text-gray-500 mb-6">
            Don't worry! Your payment was processed. You should receive a
            confirmation email shortly.
          </p>
          <a
            href="/"
            className="inline-block px-6 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 transition-colors"
          >
            Go Home
          </a>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return <Loader fullScreen message="Loading your purchase details..." />;
  }

  if (!purchase) {
    return null;
  }

  const productConfig = getProductConfig(purchase.productSku);

  // Success state matching live design with Bootstrap
  return (
    <div style={{ fontFamily: "'Open Sans', sans-serif" }}>
      {/* Main Content - Two Column Layout */}
      <div className="container">
        <div className="row">
          {/* Left Column - Thank You & Download */}
          <div className="col-lg-5 pt95 pl0-lg">
            <h2 className="cg-xml cg-bold cg-purple-4 mt0">Thank you for purchasing!</h2>
            <div className="cg-light cg-ll cg-purple-5">
              Your{' '}
              <span className="cg-regular underline capitalize">{productConfig.name}</span> is
              ready
            </div>
            <div className="cg-light cg-ll cg-purple-5 mb35">
              You may now login to manage your license(s)
            </div>

            {/* Download Button */}
            <div className="row cg-gutter">
              <div className="col-sm-8 col-lg-8">
                <div className="form-group">
                  <a href={productConfig.downloadUrl} target="_blank" rel="noopener noreferrer">
                    <button className="btn btn-cg-green btn-xl btn-block">
                      <div className="pull-left cg-regular cg-ml">Download the software</div>
                      <div className="pull-right">
                        <img src="/checkout/assets/img/download_ready.png" alt="Download" />
                      </div>
                    </button>
                  </a>
                </div>
              </div>
            </div>

            <p className="cg-purple-4">
              Your login credentials are sent to your email <sup>*</sup>
            </p>
          </div>

          {/* Right Column - What's Next */}
          <div className="col-lg-7 pt95 pl50">
            <h2 className="cg-xml cg-bold cg-purple-4 mt0 text-xs-center">What's next?</h2>
            <div className="cg-light cg-lx cg-purple-5 mb12 text-xs-center">
              Here are some articles to get you started
            </div>

            {/* Help Links */}
            <ul className="list-inline text-xs-center">
              {productConfig.helpLinks.map((link, index) => (
                <li key={index} className="hvr-sink form-group">
                  <a className="next-button" href={link.url} target="_blank" rel="noopener noreferrer">
                    <img src={link.icon} alt={link.title} className="center-block" />
                    <span>{link.title}</span>
                  </a>
                </li>
              ))}
            </ul>

            <div className="text-right text-xs-center">
              <a
                className="cg-link"
                href="https://help.pdf2xl.com/"
                target="_blank"
                rel="noopener noreferrer"
              >
                Go to knowledgebase
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Social Sharing Section */}
      <section className="pt68 pb55 cg-section-grey mt90">
        <div className="text-center cg-bold cg-xl cg-purple-4 mb20">
          TELL YOUR FRIENDS ABOUT THIS SOFTWARE
        </div>
        <ul className="list-inline text-center">
          <li className="form-group">
            <a
              href="https://www.facebook.com/sharer/sharer.php?u=pdf2xl.com"
              target="_blank"
              rel="noopener noreferrer"
            >
              <img src="/checkout/assets/img/facebook_ready.png" alt="Facebook" />
            </a>
          </li>
          <li className="form-group">
            <a
              href="https://twitter.com/home?status=Convert%20PDF%20Data%20to%20EXCEL%20Spreadsheets%20With%20The%20World's%20Most%20Powerful%20Converters%20pdf2xl.com"
              target="_blank"
              rel="noopener noreferrer"
            >
              <img src="/checkout/assets/img/twitter_ready.png" alt="Twitter" />
            </a>
          </li>
          <li className="form-group">
            <a
              href="https://www.linkedin.com/shareArticle?mini=true&url=pdf2xl.com&title=Pdf%20to%20Excel%20Converter&summary=Convert%20PDF%20Data%20to%20EXCEL%20Spreadsheets%20With%20The%20World's%20Most%20Powerful%20Converters&source=https%3A//pdf2xl.com/"
              target="_blank"
              rel="noopener noreferrer"
            >
              <img src="/checkout/assets/img/linkedin_ready.png" alt="LinkedIn" />
            </a>
          </li>
        </ul>
      </section>

      {/* Footer */}
      <section className="cg-section-purple">
        <div className="container">
          <div className="row">
            <div className="col-xs-6 col-lg-4">
              <img className="hidden-xs" src="/checkout/assets/img/ready_msg.png" alt="" />
              <span className="cg-white cg-light cg-fs15">&nbsp;&nbsp;support@pdf2xl.com</span>
            </div>
            <div className="col-xs-6 col-lg-2 col-lg-offset-6">
              <img src="/checkout/assets/img/header-logo.png" className="pull-right" alt="PDF2XL" />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ThanksPage;
